import React from 'react';
import moment from 'moment';
import ui from '../../common/services/ui';

function getSessionTypeLabel(sessionType, t) {
	switch (sessionType) {
		case 1: return t('WorkshopSessionType_Morning');
		case 2: return t('WorkshopSessionType_Afternoon');
		case 3: return t('WorkshopSessionType_FullDay');
		default: return '';
	}
}

function getUniqueChildrenSorted(childSessions) {
	const seen = new Map();
	childSessions.forEach(cs => {
		if (!seen.has(cs.child.id)) {
			seen.set(cs.child.id, cs.child);
		}
	});
	return Array.from(seen.values()).sort((a, b) =>
		a.name.toUpperCase() < b.name.toUpperCase() ? -1 : 1
	);
}

// Build (date, sessionType) columns for types that have at least one enrolment
function buildDateSessionColumns(childSessions, sessionDates) {
	const columns = [];
	const seen = new Set();
	sessionDates.forEach(date => {
		[1, 2, 3].forEach(type => {
			const key = `${date}_${type}`;
			const hasEnrolments = childSessions.some(
				cs => cs.session && cs.session.date === date && cs.session.type === type
			);
			if (hasEnrolments && !seen.has(key)) {
				seen.add(key);
				columns.push({ date, sessionType: type });
			}
		});
	});
	return columns;
}

const PrintView = ({ workshopName, weekName, childSessions, sessionDates, enableCollectMedicalInformation, t }) => {
	if (!childSessions || !sessionDates) return null;

	const children = getUniqueChildrenSorted(childSessions);
	const columns = buildDateSessionColumns(childSessions, sessionDates);
	const isSingleDate = sessionDates.length === 1;

	// Fixed columns before session date columns: #, Name
	const fixedColCount = 2;

	// Calculate colSpan per date for the date header row
	const dateGroups = sessionDates.map(date => ({
		date,
		count: columns.filter(c => c.date === date).length
	})).filter(g => g.count > 0);

	return (
		<div className='print-only'>
			<div className='print-header'>
				<p style={{ margin: '0 0 3px 0', fontSize: '10pt' }}>
					<strong>{workshopName}</strong>
					{weekName && ` · ${weekName}`}
				</p>
				<p style={{ margin: '0 0 10px 0', fontSize: '9pt', color: '#555' }}>
					{t('Attendance_PrintedOn')}: {moment().format('LL')}
				</p>
			</div>
			<table className='print-attendance-table'>
				<thead>
					{/* Date header row — only shown when displaying multiple dates */}
					{!isSingleDate && (
						<tr>
							<th colSpan={fixedColCount}></th>
							{dateGroups.map(({ date, count }) => (
								<th key={date} colSpan={count} className='center' style={{ whiteSpace: 'nowrap' }}>
									{ui.customDateFormat(date, 'D MMM')}
								</th>
							))}
						</tr>
					)}
					{/* Session type sub-header */}
					<tr>
						<th>#</th>
						<th style={{ maxWidth: 200 }}>{t('Child_FullName')}</th>
						{columns.map(({ date, sessionType }) => (
							<th key={`${date}_${sessionType}`} className='center' style={{ whiteSpace: 'nowrap' }}>
								{isSingleDate
									? `${ui.customDateFormat(date, 'D MMM')} - ${getSessionTypeLabel(sessionType, t)}`
									: getSessionTypeLabel(sessionType, t)
								}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{children.map((child, index) => (
						<tr key={child.id}>
							<td style={{ width: 30 }}>{index + 1}</td>
							<td style={{ maxWidth: 200, wordBreak: 'break-word' }}>
								<span style={{ whiteSpace: 'nowrap' }}>
									<strong>{child.name}</strong>
									{' '}
									<span style={{ fontWeight: 'normal', fontSize: '9pt', color: '#555' }}>
										{ui.formatAge(child.dateOfBirth)}
									</span>
								</span>
								{child.parent && <div style={{ fontSize: '9pt', color: '#555', whiteSpace: 'nowrap' }}>{child.parent.name}</div>}
								{enableCollectMedicalInformation && child.hasAnyMedicalInformation && (
									<div style={{ fontSize: '8pt', color: '#c00', marginTop: 2 }}>
										{child.medicalConditions && child.medicalConditions.length > 0
											? child.medicalConditions.join(', ')
											: t('Child_HasMedicalInformationWithIssuesTooltip')
										}
									</div>
								)}
							</td>
							{columns.map(({ date, sessionType }) => {
								const session = childSessions.find(
									cs => cs.child.id === child.id &&
										cs.session &&
										cs.session.date === date &&
										cs.session.type === sessionType
								);
								if (!session) {
									return <td key={`${date}_${sessionType}`} className='center' style={{ color: '#bbb' }}>—</td>;
								}
								return (
									<td key={`${date}_${sessionType}`} className='center' style={{ fontSize: '14pt' }}>
										{'☐'}
									</td>
								);
							})}
						</tr>
					))}
				</tbody>
			</table>
			<p style={{ fontSize: '9pt', marginTop: 8, color: '#555' }}>
				{t('Attendance_Print_Total')}: {children.length}
			</p>
		</div>
	);
};

export default PrintView;
